import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Award, Gem, DoorClosed, CalendarClock, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, EASE } from "@/components/site/Reveal";
import { MagneticCTA } from "@/components/site/MagneticCTA";
import { Marquee } from "@/components/site/Marquee";
import { BookingBand } from "@/components/site/BookingBand";
import { Sparkle, SparkleDivider } from "@/components/site/Sparkle";
import { IMG, STANDARDS, TESTIMONIALS, GALLERY_ITEMS, INSTAGRAM_URL, BOOKING_URL } from "@/data/site";

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });
  const px = useTransform(sx, (v) => v * -10);
  const py = useTransform(sy, (v) => v * -6);

  const onMouseMove = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const lines = ["Where Beauty", "Meets Precision"];

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="home-hero"
      className="relative h-[100svh] min-h-[620px] overflow-hidden bg-espresso flex items-center"
    >
      <motion.div className="absolute inset-0 scale-110" style={{ x: px, y: py }}>
        <div className="w-full h-full kenburns">
          <img
            src={IMG.hero}
            alt="A stylist finishing a blow-dry in warm natural light inside the S & Z studio"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-espresso/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso via-transparent to-espresso/40" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full text-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex justify-center mb-8"
        >
          <SparkleDivider light />
        </motion.div>

        <h1 className="font-display text-ivory text-[13.5vw] sm:text-7xl lg:text-[6.5rem] leading-[1.04]">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.18, duration: 1.05, ease: EASE }}
              >
                {i === 1 ? (
                  <>
                    Meets <span className="italic text-champagne">Precision</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.9, ease: EASE }}
          className="font-sans font-light text-ivory/85 text-base sm:text-lg tracking-wide max-w-xl mx-auto mt-8"
        >
          A private hair and laser studio in the heart of Brampton, crafted for
          those who value quiet luxury and lasting results.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
          className="mt-10"
        >
          <MagneticCTA href={BOOKING_URL} dark testId="hero-book-experience-cta" ariaLabel="Book an appointment (opens in a new tab)">
            Book Your Experience
          </MagneticCTA>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        aria-hidden="true"
      >
        <span className="font-sans uppercase tracking-[0.3em] text-[10px] text-ivory/50">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section data-testid="home-manifesto" className="bg-ivory py-28 sm:py-40 relative">
    <Sparkle size={18} className="absolute top-16 right-[14%] twinkle opacity-50" />
    <div className="max-w-4xl mx-auto px-6 text-center">
      <Reveal>
        <SparkleDivider />
      </Reveal>
      <Reveal delay={0.15}>
        <p className="font-quote italic font-light text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.35] text-deep mt-12">
          "At S &amp; Z, beauty is never rushed. Every visit is an appointment
          with yourself — a moment of stillness, precision, and care, delivered
          by hands you can trust."
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <p className="font-sans uppercase tracking-[0.22em] text-[11px] text-gold mt-12">
          The House Philosophy
        </p>
      </Reveal>
    </div>
  </section>
);

const ServiceTile = ({ to, image, label, title, blurb, items, span, offset, testId }) => (
  <Reveal className={`${span} ${offset || ""}`}>
    <Link to={to} data-testid={testId} className="group block">
      <div className="overflow-hidden img-frame border border-stone group-hover:border-gold transition-colors duration-500">
        <div className="overflow-hidden">
          <img
            src={image}
            alt={label}
            loading="lazy"
            className="w-full h-[300px] sm:h-[380px] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        </div>
      </div>
      <div className="mt-8">
        <Eyebrow>{label}</Eyebrow>
        <h3 className="font-display text-3xl sm:text-4xl text-espresso mt-3 group-hover:text-gold transition-colors duration-500">
          {title}
        </h3>
        <p className="text-deep/80 text-base sm:text-lg mt-4 max-w-md">{blurb}</p>
        <ul className="mt-6 space-y-2">
          {items.map((item) => (
            <li
              key={item}
              className="font-sans uppercase tracking-[0.18em] text-[11px] text-deep/60 flex items-center gap-3"
            >
              <Sparkle size={8} className="shrink-0 opacity-70" />
              {item}
            </li>
          ))}
        </ul>
        <span className="inline-flex items-center gap-2 mt-7 font-sans uppercase tracking-[0.2em] text-[11px] font-medium text-gold">
          Explore {label}
          <ArrowRight size={14} strokeWidth={1.25} className="transition-transform duration-500 group-hover:translate-x-1.5" />
        </span>
      </div>
    </Link>
  </Reveal>
);

const ServicesShowcase = () => (
  <section data-testid="home-services-showcase" className="bg-ivory py-24 sm:py-36">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16 sm:mb-20">
        <div>
          <Reveal>
            <Eyebrow>The Menu</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-espresso mt-4">
              Two Disciplines, <span className="italic text-gold">One Standard</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="font-quote italic text-xl text-deep/70 max-w-xs sm:text-right">
            Every service begins with a conversation.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14">
        <ServiceTile
          to="/services-hair"
          image={IMG.hair1}
          label="Hair"
          title="Cut. Colour. Care."
          blurb="From precision cuts to bespoke colour, every service begins with a conversation and ends with a result you'll want to keep."
          items={["Signature Cuts & Styling", "Colour & Balayage", "Keratin & Restorative Treatments", "Extensions", "Bridal & Special Occasion"]}
          span="lg:col-span-7"
          testId="service-tile-hair"
        />
        <ServiceTile
          to="/services-laser"
          image={IMG.laser1}
          label="Laser & Skin"
          title="Advanced. Gentle. Proven."
          blurb="Using medical-grade technology in a calm, private setting, our laser and skin treatments are designed around your skin, not a one-size-fits-all program."
          items={["Laser Hair Removal", "Skin Rejuvenation", "Medi-Facials", "Pigmentation & Texture Treatments"]}
          span="lg:col-span-5"
          offset="lg:mt-32"
          testId="service-tile-laser"
        />
      </div>
    </div>
  </section>
);

const ICONS = [Award, Gem, DoorClosed, CalendarClock];

const Standard = () => (
  <section data-testid="home-standard" className="bg-espresso py-24 sm:py-36 relative overflow-hidden">
    <Sparkle size={22} className="absolute top-14 left-[8%] twinkle opacity-40" />
    <Sparkle size={14} className="absolute bottom-20 right-[10%] twinkle opacity-30" />
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="text-center max-w-2xl mx-auto mb-16 sm:mb-24">
        <Reveal>
          <Eyebrow light>Why S &amp; Z</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory mt-4">
            The S &amp; Z <span className="italic text-gold">Standard</span>
          </h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
        {STANDARDS.map((s, i) => {
          const Icon = ICONS[i];
          return (
            <Reveal key={s.title} delay={0.12 * i} data-testid={`standard-pillar-${i}`}>
              <div className="text-center lg:text-left border-t border-gold/30 pt-8">
                <Icon size={30} strokeWidth={0.9} className="text-gold mx-auto lg:mx-0" />
                <h3 className="font-display text-2xl text-ivory mt-6">{s.title}</h3>
                <p className="text-ivory/60 text-sm mt-4 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const GalleryPreview = () => {
  const items = GALLERY_ITEMS.slice(0, 5);
  return (
    <section data-testid="home-gallery-preview" className="bg-ivory py-24 sm:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal>
              <Eyebrow>The Portfolio</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-espresso mt-4">
                Results, <span className="italic text-gold">Not Promises</span>
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="text-deep/70 text-base sm:text-lg mt-4">
                A closer look at the work we're proud of.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/gallery"
              data-testid="gallery-preview-view-all"
              className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.2em] text-[11px] font-medium text-gold hover:text-espresso transition-colors duration-300"
            >
              View the Gallery <ArrowUpRight size={14} strokeWidth={1.25} />
            </Link>
          </Reveal>
        </div>
      </div>
      <Reveal delay={0.15}>
        <div
          data-testid="gallery-preview-scroller"
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory px-6 sm:px-10 lg:px-16 pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((g, i) => (
            <Link
              to="/gallery"
              key={g.title}
              data-testid={`gallery-preview-item-${i}`}
              className="group snap-start shrink-0 w-[75vw] sm:w-[380px]"
            >
              <div className="overflow-hidden img-frame border border-stone group-hover:border-gold group-hover:-translate-y-1.5 transition-all duration-500">
                <img
                  src={g.src}
                  alt={g.title}
                  loading="lazy"
                  className="w-full h-[300px] sm:h-[420px] object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <p className="font-sans uppercase tracking-[0.2em] text-[11px] text-deep/60 mt-4">
                {g.title}
              </p>
            </Link>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const TestimonialCarousel = () => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % 3), 6000);
    return () => clearInterval(t);
  }, []);
  const items = TESTIMONIALS.slice(0, 3);
  const current = items[index];

  return (
    <section data-testid="home-testimonials" className="bg-ivory pb-24 sm:pb-36 pt-4">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <SparkleDivider />
        </Reveal>
        <div className="min-h-[220px] sm:min-h-[200px] flex items-center justify-center mt-10">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.8, ease: EASE }}
              data-testid="testimonial-slide"
            >
              <p className="font-quote italic font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.4] text-deep">
                "{current.quote}"
              </p>
              <footer className="font-sans uppercase tracking-[0.22em] text-[11px] text-gold mt-8">
                — {current.name}, {current.meta}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="flex justify-center gap-3 mt-8">
          {items.map((_, i) => (
            <button
              key={i}
              data-testid={`testimonial-dot-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Show testimonial ${i + 1}`}
              className={`h-1 transition-all duration-500 ${
                i === index ? "w-10 bg-gold" : "w-4 bg-stone hover:bg-champagne"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const InstagramStrip = () => {
  const tiles = [IMG.hair3, IMG.spa2, IMG.hair7, IMG.interior2, IMG.laser3, IMG.hair4];
  return (
    <section data-testid="home-instagram-strip" className="bg-ivory pb-24 sm:pb-36">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <Reveal className="text-center mb-12">
          <Eyebrow>
            <span className="mx-auto">Follow the Studio</span>
          </Eyebrow>
          <h2 className="font-display text-3xl sm:text-4xl text-espresso mt-4">
            Life Inside the <span className="italic text-gold">Atelier</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {tiles.map((src, i) => (
            <Reveal key={i} delay={0.06 * i}>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-tile-${i}`}
                aria-label="Open S & Z on Instagram"
                className="group block overflow-hidden border border-stone hover:border-gold transition-colors duration-500"
              >
                <img
                  src={src}
                  alt="Inside the S & Z studio"
                  loading="lazy"
                  className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const GoogleReviews = () => (
  <section data-testid="google-reviews-section" className="bg-ivory pb-24 sm:pb-36">
    <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
      <Reveal className="text-center mb-12">
        <Eyebrow>
          <span className="mx-auto">Reviews from Google</span>
        </Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl text-espresso mt-4">
          Kind <span className="italic text-gold">Words</span>
        </h2>
      </Reveal>
      <Reveal>
        {/* Live Elfsight Google Reviews widget — pulls from the client's real Google Business Profile and updates automatically.
            platform.js is loaded once in index.html. If the embed does not render inside the preview sandbox,
            leave this container in place — it will render on the live site. Do not replace with placeholder reviews. */}
        <div className="elfsight-app-d9dd9941-89e5-4ed1-8b30-9ca126a821ca min-h-[320px]" data-elfsight-app-lazy />
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Manifesto />
      <Marquee dark />
      <ServicesShowcase />
      <Standard />
      <GalleryPreview />
      <TestimonialCarousel />
      <GoogleReviews />
      <InstagramStrip />
      <BookingBand />
    </>
  );
}
